import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Heading } from '@/lib/rich-text'
import type { Resource, ResourceSummary } from '@/types'

import Link from 'next/link'
import { ArrowLeft, Clock } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { plainText, withHeadingIds } from '@/lib/rich-text'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { RichText } from '@/components/resources/rich-text'
import { ResourceCard, ResourceCover } from '@/components/resources/resource-card'

import {
    ReadingProgress,
    ShareLinks,
    TableOfContents
} from '@/components/resources/article-tools'

import {
    getResourceBySlug,
    getResourceSlugs,
    getResources,
    safely
} from '@/lib/content'

interface ArticleProps {
    resource: Resource
    html: string
    headings: Array<Heading>
    related: Array<ResourceSummary>
}

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

const Article: FC<ArticleProps> = ({ resource, html, headings, related }): ReactNode => {
    const description = resource.excerpt ?? plainText(html).slice(0, 155)

    return (
        <Layout>
            <Seo
                title={resource.title}
                description={description}
                ogImage={resource.coverImage ?? undefined}
            />

            <ReadingProgress targetId='article-body' />

            <article>
                <header className='bg-cream'>
                    <Container className='pb-12 pt-32 md:pb-16 md:pt-40'>
                        <div className='mx-auto max-w-4xl'>
                            <Link
                                href='/resources'
                                className='animate-rise group inline-flex items-center gap-2 text-sm font-medium text-stone-dark transition-colors hover:text-burgundy'
                                style={delay(0)}
                            >
                                <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-0.5' />
                                All resources
                            </Link>

                            <div className='animate-rise mt-10' style={delay(80)}>
                                <Eyebrow>{resource.category?.name ?? 'Article'}</Eyebrow>
                            </div>

                            <h1 className='animate-rise mt-6 font-heading text-display-lg font-medium text-burgundy' style={delay(140)}>
                                {resource.title}
                            </h1>

                            {resource.excerpt && (
                                <p className='animate-rise mt-6 max-w-2xl text-lg leading-relaxed text-stone-dark md:text-xl' style={delay(220)}>
                                    {resource.excerpt}
                                </p>
                            )}

                            <div className='animate-rise mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-burgundy/10 pt-6 text-sm text-stone-dark' style={delay(280)}>
                                {resource.authorName && (
                                    <span>
                                        <span className='font-medium text-burgundy'>{resource.authorName}</span>
                                        {resource.authorRole && <span className='text-stone'>, {resource.authorRole}</span>}
                                    </span>
                                )}

                                {resource.publishedAt && <time dateTime={resource.publishedAt}>{formatDate(resource.publishedAt)}</time>}

                                {resource.readMinutes ? (
                                    <span className='inline-flex items-center gap-1.5'>
                                        <Clock aria-hidden='true' className='size-3.5' />
                                        {resource.readMinutes} min read
                                    </span>
                                ) : null}
                            </div>
                        </div>

                        {resource.coverImage && (
                            <div className='animate-rise mx-auto mt-12 max-w-6xl' style={delay(360)}>
                                <ResourceCover resource={resource} eager className='aspect-[16/8] rounded-[28px]' />
                            </div>
                        )}
                    </Container>
                </header>

                <div className='bg-cream'>
                    <Container className='pb-24 md:pb-32'>
                        <div className='mx-auto grid max-w-6xl gap-12 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-20'>
                            <div id='article-body' className='min-w-0 lg:pt-6'>
                                {html ? (
                                    <RichText html={html} className='mx-auto max-w-[680px] text-burgundy' />
                                ) : (
                                    <p className='text-stone-dark'>This article is being written. Check back soon.</p>
                                )}
                            </div>

                            <aside className='lg:pt-6'>
                                <div className='space-y-10 lg:sticky lg:top-28'>
                                    <TableOfContents headings={headings} />
                                    <ShareLinks title={resource.title} />

                                    <div className='rounded-3xl bg-burgundy p-6 text-cream'>
                                        <p className='font-heading text-xl font-medium leading-snug'>See your own numbers in Veyra.</p>
                                        <p className='mt-2 text-sm leading-relaxed text-cream/70'>A 30-minute walkthrough with your accounts connected.</p>

                                        <ButtonLink href='/demo' variant='light' size='sm' className='mt-5' arrow>
                                            Request demo
                                        </ButtonLink>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    </Container>
                </div>
            </article>

            {related.length > 0 && (
                <section aria-labelledby='related-title' className='border-t border-burgundy/10 bg-paper'>
                    <Container className='py-24 md:py-32'>
                        <div className='flex flex-wrap items-end justify-between gap-6'>
                            <h2 id='related-title' className='font-heading text-display-md font-medium text-burgundy'>Keep reading</h2>

                            <ButtonLink href='/resources' variant='secondary' arrow>
                                All resources
                            </ButtonLink>
                        </div>

                        <ul className='mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map((item, index) => (
                                <li key={item.id} data-reveal='' style={{ '--reveal-delay': `${index * 90}ms` } as CSSProperties}>
                                    <ResourceCard resource={item} />
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await safely(getResourceSlugs, [], 'resource slugs')

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ArticleProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 60 }

    let resource: Resource | null = null

    try {
        resource = await getResourceBySlug(slug)
    }
    catch (error) {
        console.error(`Could not load the article "${slug}":`, error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 10 }
    }

    if (!resource) return { notFound: true, revalidate: 60 }

    const { html, headings } = withHeadingIds(resource.body ?? '')
    const others = await safely(getResources, [], 'related resources')
    const candidates = others.filter(item => item.id !== resource.id)
    const sameCategory = candidates.filter(item => item.category?.id && item.category.id === resource.category?.id)
    const related = [...sameCategory, ...candidates.filter(item => !sameCategory.includes(item))].slice(0, 3)

    return {
        props: { resource, html, headings, related },
        revalidate: 60
    }
}

export default Article
